/**
 * tm/components/GovWatchSection.tsx — 업체 업무 탭 「기다리는 공고」 + 「공고 나오면 알림」 입력 (2026-10-30 지도사
 *  「중소기업혁신바우처 2027 공고가 나오면 신청하기로 했는데 나에게 어떻게 알림을 — 담당 FP 에게도 · 누락 없고 실수 없도록」)
 *
 *  - 기다리는 공고 카드: 기다리는 중 · 공고 나옴(확인 안 함) · 업무 만듦 · 그만 — 나온 공고 · 문자 결과(보냄 · 9시에 보냄 · 못 보냄 까닭)
 *  - 입력: 사업 찾기(등록 공고 + 수집 공고 묶음) → 찾을 말 · 몇 년 공고부터 · 메모 · 문자(나 · 담당 FP)
 *          → 「지난 1년 이렇게 잡혔어요」 미리 보기로 찾을 말이 맞는지 눈으로 확인 · 이미 접수 중인 공고가 있으면 알려 줌(알림은 기다리기 시작한 뒤 공고만)
 *  - 판정 · 문자는 서버(services/govWatch) 한 곳 — 화면은 보여 주기만
 *  - 글자 11/12/13 · 단추 높이 28 · 같은 줄 단추 폭 같게 · 왼쪽 색 막대 없음
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as api from '../api';
import type { GovWatch, GovWatchHit, GovWatchList, GovWatchPreview, GovWatchSuggest, GovWatchSms } from '../api';
import { C, T, R, IMEText } from './ui';

const BTN_W = 96;
const btn = (kind: 'primary' | 'plain' | 'danger' = 'plain', w: number | string = BTN_W): React.CSSProperties => ({
  width: w, height: 28, padding: '0 8px', borderRadius: R.sm, fontSize: T.xs, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', boxSizing: 'border-box', whiteSpace: 'nowrap',
  border: `1px solid ${kind === 'primary' ? C.accent : kind === 'danger' ? '#FCA5A5' : C.border}`,
  background: kind === 'primary' ? C.accent : C.white, color: kind === 'primary' ? C.white : kind === 'danger' ? C.red : C.sub,
});
const chip = (on: boolean): React.CSSProperties => ({
  height: 26, padding: '0 10px', borderRadius: 13, border: `1px solid ${on ? C.accent : C.border}`, background: on ? C.accentLt : C.white,
  color: on ? C.accent : C.sub, fontSize: T.xs, fontWeight: on ? 700 : 500, cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap',
});
const lbl: React.CSSProperties = { fontSize: T.xs, fontWeight: 700, color: C.sub, marginBottom: 4 };
const input: React.CSSProperties = { width: '100%', boxSizing: 'border-box', height: 30, padding: '0 10px', borderRadius: R.sm, border: `1px solid ${C.border}`, fontSize: T.sm, fontFamily: 'inherit', outline: 'none' };

function kstNow() { return new Date(Date.now() + 9 * 3600 * 1000); }
/** 처음 고를 연도 — 하반기면 내년 공고(올해 공고는 거의 다 나옴) */
function defaultYear() { const d = kstNow(); return d.getUTCMonth() >= 6 ? d.getUTCFullYear() + 1 : d.getUTCFullYear(); }
function md(s?: string | null) { if (!s) return ''; const d = s.slice(0, 10); return `${+d.slice(5, 7)}/${+d.slice(8, 10)}`; }
function whenKst(iso?: string) {
  if (!iso) return '';
  const d = new Date(new Date(iso).getTime() + 9 * 3600 * 1000);
  const W = ['일', '월', '화', '수', '목', '금', '토'];
  return `${d.getUTCMonth() + 1}/${d.getUTCDate()}(${W[d.getUTCDay()]}) ${d.getUTCHours()}시`;
}
/** 문자 결과 한 줄 */
export function smsLine(s: GovWatchSms | null): { text: string; tone: 'ok' | 'wait' | 'bad' | 'mute' } {
  if (!s) return { text: '', tone: 'mute' };
  const nm = (x: { key: string; name: string }) => (x.key === 'me' ? '나' : `${x.name} FP`);
  const names = (st: string) => s.list.filter(x => x.state === st).map(nm);
  if (s.state === 'merged') return { text: '문자: 같은 공고라 다시 안 보냄', tone: 'mute' };
  if (s.state === 'off') return { text: '문자 안 보내기로 함', tone: 'mute' };
  if (s.state === 'cancelled') return { text: '문자: 알림을 끝내서 안 보냄', tone: 'mute' };
  if (s.state === 'pending' || s.state === 'sending') return { text: `문자: ${whenKst(s.due)}에 보냄 — ${names('pending').concat(names('sending')).join(' · ')}`, tone: 'wait' };
  const parts = s.list.map(x => (x.state === 'sent' ? `${nm(x)} 보냄` : `${nm(x)} 못 보냄(${x.error || '실패'})`));
  return { text: parts.length ? `문자: ${parts.join(' · ')}` : '', tone: s.list.some(x => x.state !== 'sent') ? 'bad' : 'ok' };
}
const TONE = { ok: C.green, wait: C.accent, bad: C.red, mute: C.muted } as const;

interface FormSeed { programId?: string }
interface Props {
  companyId: string;
  /** null = 입력 닫힘 · {} = 빈 입력 · { programId } = 추천 공고에서 「다음 공고 알림」 */
  form: FormSeed | null;
  onCloseForm: () => void;
  /** 「신청 업무 만들기」 — 업무 등록을 열고, 저장되면 이 알림을 끝낸다(CompanyDetailPanel) */
  onMakeTask: (w: GovWatch) => void;
  /** 대시보드에서 온 기다림 — 잠깐 강조 */
  focusId?: string | null;
  /** 업무 등록 · 알림 끝 뒤 다시 불러오기 */
  reloadKey?: number;
  onToast: (msg: string, type?: 'success' | 'error') => void;
}

export default function GovWatchSection({ companyId, form, onCloseForm, onMakeTask, focusId, reloadKey, onToast }: Props) {
  const [data, setData] = useState<GovWatchList | null>(null);
  const [err, setErr] = useState('');
  const [edit, setEdit] = useState<GovWatch | null>(null);
  const load = useCallback(() => {
    api.getGovWatches(companyId).then(r => { setData(r.data || null); setErr(''); }).catch((e: unknown) => setErr(e instanceof Error ? e.message : '불러오지 못했어요'));
  }, [companyId]);
  useEffect(() => { setData(null); setEdit(null); load(); }, [load]);
  useEffect(() => { if (reloadKey) load(); }, [reloadKey, load]);

  const watches = data ? data.watches : [];
  const open = !!form || !!edit;
  if (!open && !watches.length && !err) return null;

  async function act(fn: () => Promise<unknown>, okMsg: string) {
    try { await fn(); onToast(okMsg, 'success'); load(); } catch (e: unknown) { onToast(e instanceof Error ? e.message : '저장하지 못했어요', 'error'); }
  }

  return (
    <div style={{ marginTop: 12 }}>
      <div style={{ fontSize: 11, color: C.muted, marginBottom: 6, fontWeight: 600, letterSpacing: 0.3 }}>기다리는 공고{watches.length ? ` ${watches.filter(w => w.status === 'on').length}` : ''}</div>
      {err && <div style={{ fontSize: T.xs, color: C.red, marginBottom: 6 }}>{err} <button type="button" onClick={load} style={{ ...btn(), width: 'auto', height: 22 }}>다시 시도</button></div>}
      {data && !data.ready && <div style={{ fontSize: T.xs, color: C.orange, marginBottom: 6 }}>배포가 끝나면 쓸 수 있어요</div>}
      {watches.map(w => (edit && edit.id === w.id ? null : (
        <WatchCard key={w.id} w={w} fps={data ? data.fps : []} focus={focusId === w.id}
          onMakeTask={() => onMakeTask(w)}
          onSeen={() => void act(() => api.seenGovWatch(w.id), '확인했어요')}
          onEdit={() => { setEdit(w); onCloseForm(); }}
          onStop={() => void act(() => api.updateGovWatch(w.id, { status: 'off' }), '알림을 그만했어요 — 다시 기다리기로 되돌릴 수 있어요')}
          onResume={() => void act(() => api.updateGovWatch(w.id, { status: 'on' }), '다시 기다려요')}
          onDelete={() => void act(() => api.deleteGovWatch(w.id), '지웠어요')} />
      )))}
      {open && data && (
        <WatchForm key={edit ? edit.id : `new-${form && form.programId ? form.programId : ''}`} companyId={companyId} seed={form} edit={edit} list={data}
          onCancel={() => { setEdit(null); onCloseForm(); }}
          onSaved={(msg) => { setEdit(null); onCloseForm(); onToast(msg, 'success'); load(); }}
          onError={(m) => onToast(m, 'error')} />
      )}
    </div>
  );
}

/* ── 기다리는 공고 카드 ── */
function WatchCard({ w, fps, focus, onMakeTask, onSeen, onEdit, onStop, onResume, onDelete }: {
  w: GovWatch; fps: GovWatchList['fps']; focus: boolean; onMakeTask: () => void; onSeen: () => void; onEdit: () => void; onStop: () => void; onResume: () => void; onDelete: () => void;
}) {
  const [showNoise, setShowNoise] = useState(false);
  const [armDel, setArmDel] = useState(false);   // 지우기 — 두 번 눌러야(확인 창 없이 · 3초 뒤 풀림)
  useEffect(() => { if (!armDel) return undefined; const t = setTimeout(() => setArmDel(false), 3000); return () => clearTimeout(t); }, [armDel]);
  const ref = useRef<HTMLDivElement | null>(null);
  useEffect(() => { if (focus && ref.current) ref.current.scrollIntoView({ block: 'center', behavior: 'smooth' }); }, [focus]);
  const hits = w.hits || [];
  const notices = hits.filter(h => h.kind === 'notice');
  const noise = hits.filter(h => h.kind === 'noise');
  const fresh = notices.filter(h => !h.seen_at);
  const st = w.status === 'on' ? (fresh.length ? 'hit' : 'wait') : w.status;
  const badge = { hit: ['공고 나옴', C.green, C.greenLt], wait: ['기다리는 중', C.accent, C.accentLt], done: ['업무 만듦', C.sub, C.bg], off: ['그만', C.sub, C.bg] }[st] as [string, string, string];
  const who = [w.notify_me ? '나' : '', w.notify_fp ? (fps.length ? fps.map(f => `${f.name} FP`).join(' · ') : '담당 FP') : ''].filter(Boolean);
  const warn: string[] = [];
  if (w.status === 'on' && w.notify_me && w.owner_phone === false) warn.push('설정 › 아침 문자 받을 번호를 넣어야 내 문자가 와요');
  if (w.status === 'on' && w.notify_fp && !fps.length) warn.push('담당 FP가 없어 FP 문자는 안 가요');
  if (w.status === 'on' && w.notify_fp) fps.filter(f => !f.has_phone).forEach(f => warn.push(`${f.name} FP 연락처가 없어 문자가 안 가요`));
  const live = w.status === 'on';
  return (
    <div ref={ref} style={{ borderRadius: R.md, border: `1px solid ${st === 'hit' || focus ? C.green : C.border}`, background: live ? C.white : C.bgAlt, padding: '10px 12px', marginBottom: 8, boxShadow: focus ? `0 0 0 3px ${C.greenLt}` : 'none' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span style={{ fontSize: T.md, fontWeight: 700, color: live ? C.text : C.sub, minWidth: 0 }}>📣 {w.name}</span>
        <span style={{ padding: '1px 8px', borderRadius: 10, fontSize: 10, fontWeight: 700, color: badge[1], background: badge[2] }}>{badge[0]}</span>
        <span style={{ marginLeft: 'auto', fontSize: T.xs, color: C.muted }}>{w.target_year ? `${w.target_year}년 공고부터` : '연도 상관없이'} · {md(w.since)}부터 기다림</span>
      </div>
      <div style={{ fontSize: T.xs, color: C.sub, marginTop: 4, lineHeight: 1.6 }}>
        찾을 말 {w.keys.map(k => `「${k}」`).join(' ')} · 문자 {who.length ? who.join(' · ') : '안 보냄'}{w.memo ? ` · 메모 ${w.memo}` : ''}
      </div>
      {warn.map(x => <div key={x} style={{ fontSize: T.xs, color: C.orange, marginTop: 2 }}>{x}</div>)}

      {notices.length > 0 && (
        <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
          {notices.slice(0, 5).map(h => <HitLine key={h.id} h={h} />)}
        </div>
      )}
      {noise.length > 0 && (
        <div style={{ marginTop: 6 }}>
          <button type="button" onClick={() => setShowNoise(v => !v)} style={{ border: 'none', background: 'none', padding: 0, fontSize: T.xs, color: C.muted, cursor: 'pointer', fontFamily: 'inherit' }}>
            관련 소식 {noise.length}건 · 결과 발표 · 설명회 등(문자 없음) {showNoise ? '접기' : '보기'}
          </button>
          {showNoise && <div style={{ marginTop: 4, display: 'flex', flexDirection: 'column', gap: 4 }}>{noise.slice(0, 10).map(h => <HitLine key={h.id} h={h} />)}</div>}
        </div>
      )}

      <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', flexWrap: 'wrap', marginTop: 10 }}>
        {live && fresh.length > 0 && <button type="button" onClick={onMakeTask} style={btn('primary')}>신청 업무 만들기</button>}
        {live && fresh.length > 0 && <button type="button" onClick={onSeen} style={btn()}>확인</button>}
        {live && !fresh.length && <button type="button" onClick={onEdit} style={btn()}>고치기</button>}
        {live && !fresh.length && <button type="button" onClick={onStop} style={btn()}>그만 알림</button>}
        {!live && <button type="button" onClick={onResume} style={btn()}>다시 기다리기</button>}
        {!live && <button type="button" onClick={() => { if (armDel) { setArmDel(false); onDelete(); } else setArmDel(true); }} title="받은 알림 기록도 같이 지워져요" style={btn('danger')}>{armDel ? '한 번 더 누르면' : '지우기'}</button>}
      </div>
    </div>
  );
}

function HitLine({ h }: { h: GovWatchHit }) {
  const s = smsLine(h.sms);
  const noise = h.kind === 'noise';
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '6px 8px', borderRadius: R.sm, background: noise ? C.bgAlt : h.seen_at ? C.bgAlt : C.greenLt }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: T.xs, fontWeight: 700, color: noise ? C.sub : C.text, lineHeight: 1.45, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>{h.title}</div>
        <div style={{ fontSize: T.xs, color: C.sub, marginTop: 2 }}>
          {noise ? `${h.noise_why} · ` : ''}접수 {h.period}{h.agency ? ` · ${h.agency}` : ''} · {md(h.created_at)} 들어옴
        </div>
        {s.text && <div style={{ fontSize: T.xs, color: TONE[s.tone], marginTop: 1 }}>{s.text}</div>}
      </div>
      {h.url ? <a href={h.url} target="_blank" rel="noopener noreferrer" style={{ ...btn(), width: 76, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', textDecoration: 'none', flexShrink: 0 }}>공고 보기</a> : null}
    </div>
  );
}

/* ── 입력 ── */
function WatchForm({ companyId, seed, edit, list, onCancel, onSaved, onError }: {
  companyId: string; seed: FormSeed | null; edit: GovWatch | null; list: GovWatchList; onCancel: () => void; onSaved: (msg: string) => void; onError: (m: string) => void;
}) {
  const y0 = kstNow().getUTCFullYear();
  const [q, setQ] = useState('');
  const [sugs, setSugs] = useState<GovWatchSuggest[] | null>(null);
  const [searching, setSearching] = useState(false);
  const [picked, setPicked] = useState<boolean>(!!edit);
  const [name, setName] = useState(edit ? edit.name : '');
  const [agency, setAgency] = useState('');
  const [keys, setKeys] = useState<string[]>(edit ? edit.keys : []);
  const [keyIn, setKeyIn] = useState('');
  const [groupIds, setGroupIds] = useState<string[]>(edit ? edit.group_ids : []);
  const [programIds, setProgramIds] = useState<string[]>(edit ? edit.program_ids : []);
  const [year, setYear] = useState<number | null>(edit ? edit.target_year : defaultYear());
  const [memo, setMemo] = useState(edit ? edit.memo : '');
  const [notifyMe, setNotifyMe] = useState(edit ? edit.notify_me : true);
  const [notifyFp, setNotifyFp] = useState(edit ? edit.notify_fp : list.fps.length > 0);
  const [pv, setPv] = useState<GovWatchPreview[] | null>(null);
  const [saving, setSaving] = useState(false);

  const choose = useCallback((s: GovWatchSuggest) => {
    setPicked(true); setName(s.label); setAgency(s.agency); setKeys(s.keys.length ? s.keys : [s.label]); setGroupIds(s.group_ids); setProgramIds(s.program_ids); setSugs(null);
  }, []);
  // 추천 공고에서 왔으면 그 사업으로 채운다
  useEffect(() => {
    if (!seed || !seed.programId || edit) return;
    api.suggestGovWatch({ program_id: seed.programId }).then(r => { const s = (r.data || [])[0]; if (s) choose(s); }).catch(() => { /* 직접 찾기로 */ });
  }, [seed, edit, choose]);
  // 사업 찾기 — 0.3초 쉬면
  useEffect(() => {
    if (picked) return undefined;
    const s = q.trim();
    if (s.length < 2) { setSugs(null); return undefined; }
    const t = setTimeout(() => {
      setSearching(true);
      api.suggestGovWatch({ q: s }).then(r => setSugs(r.data || [])).catch(() => setSugs([])).finally(() => setSearching(false));
    }, 300);
    return () => clearTimeout(t);
  }, [q, picked]);
  // 미리 보기 — 찾을 말 · 묶음 · 연도가 바뀌면
  const pvKey = JSON.stringify([keys, groupIds, year]);
  useEffect(() => {
    if (!keys.length && !groupIds.length) { setPv(null); return undefined; }
    const t = setTimeout(() => { api.previewGovWatch({ keys, group_ids: groupIds, target_year: year }).then(r => setPv(r.data || [])).catch(() => setPv(null)); }, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pvKey]);

  const openNow = useMemo(() => (pv || []).filter(p => p.open && !p.old && !p.noise), [pv]);
  function addKey() {
    const k = keyIn.replace(/\s+/g, ' ').trim();
    if (!k) return;
    if (k.replace(/[^0-9a-zA-Z가-힣]/g, '').length < 3) { onError('찾을 말은 3글자 이상으로 넣어 주세요'); return; }
    if (!keys.some(x => x.replace(/\s/g, '') === k.replace(/\s/g, ''))) setKeys([...keys, k].slice(0, 6));
    setKeyIn('');
  }
  async function save() {
    const nm = name.trim();
    if (!nm) { onError('사업을 골라 주세요'); return; }
    const ks = keys.length ? keys : [nm];
    setSaving(true);
    try {
      const body = { name: nm, keys: ks, group_ids: groupIds, program_ids: programIds, target_year: year, memo: memo.trim(), notify_me: notifyMe, notify_fp: notifyFp };
      if (edit) { await api.updateGovWatch(edit.id, body); onSaved('고쳤어요'); }
      else { const r = await api.createGovWatch({ ...body, company_id: companyId }); onSaved(r.data && r.data.merged ? '이미 기다리던 사업이라 그 줄을 고쳤어요' : '공고가 나오면 알려 드릴게요'); }
    } catch (e: unknown) { onError(e instanceof Error ? e.message : '저장하지 못했어요'); }
    finally { setSaving(false); }
  }

  const years: (number | null)[] = [null, y0, y0 + 1, y0 + 2];
  const fpNames = list.fps.map(f => f.name).join(' · ');
  return (
    <div style={{ borderRadius: R.md, border: `1px solid ${C.accent}`, background: C.white, padding: 12, marginBottom: 8 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', flexWrap: 'wrap', gap: '2px 8px', marginBottom: 10 }}>
        <b style={{ fontSize: T.md, color: C.text, whiteSpace: 'nowrap' }}>{edit ? '기다리는 공고 고치기' : '공고 나오면 알림'}</b>
        <span style={{ fontSize: T.xs, color: C.muted }}>새 공고가 수집되면 화면과 문자로 알려요</span>
      </div>

      {/* 1 사업 */}
      <div style={{ marginBottom: 10 }}>
        <div style={lbl}>사업</div>
        {picked ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <IMEText value={name} onChange={setName} aria-label="사업 이름" style={{ ...input, flex: '1 1 220px', width: 'auto' }} />
            {agency && <span style={{ fontSize: T.xs, color: C.muted, whiteSpace: 'nowrap' }}>{agency}</span>}
            {!edit && <button type="button" onClick={() => { setPicked(false); setQ(name); }} style={btn('plain', 76)}>다시 찾기</button>}
          </div>
        ) : (
          <>
            <IMEText value={q} onChange={setQ} autoFocus placeholder="사업 이름으로 찾기 (예: 혁신바우처)" aria-label="사업 찾기" style={input} />
            {searching && <div style={{ fontSize: T.xs, color: C.muted, marginTop: 4 }}>찾는 중…</div>}
            {sugs && (
              <div style={{ marginTop: 4, border: `1px solid ${C.border}`, borderRadius: R.sm, maxHeight: 220, overflowY: 'auto' }}>
                {sugs.map(s => (
                  <button key={s.label + s.program_ids.join() + s.group_ids.join()} type="button" onClick={() => choose(s)}
                    style={{ display: 'flex', width: '100%', gap: 8, alignItems: 'center', padding: '7px 10px', border: 'none', borderBottom: `1px solid ${C.border}`, background: C.white, cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit' }}>
                    <span style={{ flex: 1, minWidth: 0, fontSize: T.sm, fontWeight: 600, color: C.text }}>{s.label}</span>
                    <span style={{ fontSize: T.xs, color: C.muted, whiteSpace: 'nowrap' }}>{s.agency}{s.latest ? ` · ${s.latest.slice(0, 7).replace('-', '.')}` : ''}</span>
                  </button>
                ))}
                {q.trim().length >= 2 && (
                  <button type="button" onClick={() => choose({ label: q.trim(), agency: '', keys: [q.trim()], program_ids: [], group_ids: [], latest: '' })}
                    style={{ display: 'block', width: '100%', padding: '7px 10px', border: 'none', background: C.bgAlt, cursor: 'pointer', textAlign: 'left', fontSize: T.sm, color: C.accent, fontWeight: 600, fontFamily: 'inherit' }}>
                    {sugs.length ? '' : '찾은 사업이 없어요 · '}「{q.trim()}」 이름 그대로 기다리기
                  </button>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {picked && (
        <>
          {/* 2 찾을 말 */}
          <div style={{ marginBottom: 10 }}>
            <div style={lbl}>찾을 말 <span style={{ fontWeight: 400, color: C.muted }}>— 공고 제목에 이 말이 들어 있으면 알려요(띄어쓰기 · 연도 · 차수는 상관없어요)</span></div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
              {keys.map(k => (
                <span key={k} style={{ ...chip(true), display: 'inline-flex', alignItems: 'center', gap: 4, cursor: 'default' }}>
                  {k}
                  <button type="button" aria-label={`${k} 빼기`} onClick={() => setKeys(keys.filter(x => x !== k))} style={{ border: 'none', background: 'none', color: C.accent, cursor: 'pointer', padding: 0, fontSize: T.sm, lineHeight: 1 }}>✕</button>
                </span>
              ))}
              <IMEText value={keyIn} onChange={setKeyIn} placeholder="+ 말 더하기 (예: 혁신바우처)" aria-label="찾을 말 더하기"
                onKeyDown={e => { if (e.key === 'Enter' && !e.nativeEvent.isComposing) { e.preventDefault(); addKey(); } }}
                onBlur={() => { if (keyIn.trim()) addKey(); }}
                style={{ ...input, width: 190, height: 26 }} />
            </div>
          </div>

          {/* 3 연도 · 4 메모 */}
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 10 }}>
            <div>
              <div style={lbl}>몇 년 공고부터</div>
              <div style={{ display: 'flex', gap: 6 }}>
                {years.map(y => <button key={String(y)} type="button" onClick={() => setYear(y)} style={chip(year === y)}>{y ? `${y}년` : '연도 상관없이'}</button>)}
              </div>
            </div>
            <div style={{ flex: '1 1 200px' }}>
              <div style={lbl}>메모</div>
              <IMEText value={memo} onChange={setMemo} maxLength={200} placeholder="예: 2027 신청하기로 함" aria-label="메모" style={input} />
            </div>
          </div>

          {/* 5 문자 */}
          <div style={{ marginBottom: 10 }}>
            <div style={lbl}>문자로도 알림 <span style={{ fontWeight: 400, color: C.muted }}>— 근무일 9~18시, 밤 · 주말이면 다음 근무일 9시</span></div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              <button type="button" onClick={() => setNotifyMe(v => !v)} style={chip(notifyMe)}>{notifyMe ? '✓ ' : ''}나</button>
              <button type="button" onClick={() => setNotifyFp(v => !v)} disabled={!list.fps.length} style={{ ...chip(notifyFp && list.fps.length > 0), opacity: list.fps.length ? 1 : 0.5, cursor: list.fps.length ? 'pointer' : 'default' }}>
                {notifyFp && list.fps.length ? '✓ ' : ''}담당 FP{fpNames ? ` ${fpNames}` : ' 없음'}
              </button>
            </div>
            {notifyMe && !list.my_phone && <div style={{ fontSize: T.xs, color: C.orange, marginTop: 4 }}>내 번호가 없어요 — 설정 › 아침 문자 받을 번호를 넣어야 문자가 와요(화면 알림은 그대로)</div>}
            {notifyFp && list.fps.filter(f => !f.has_phone).map(f => <div key={f.id} style={{ fontSize: T.xs, color: C.orange, marginTop: 2 }}>{f.name} FP 연락처가 없어 문자가 안 가요</div>)}
          </div>

          {/* 6 미리 보기 */}
          <div style={{ marginBottom: 12, padding: 10, borderRadius: R.sm, background: C.bgAlt, border: `1px solid ${C.border}` }}>
            <div style={{ fontSize: T.xs, fontWeight: 700, color: C.sub, marginBottom: 2 }}>지난 1년, 이 말로 이렇게 잡혔어요</div>
            <div style={{ fontSize: T.xs, color: C.muted, marginBottom: 6 }}>{year ? `같은 이름으로 ${year}년 이후 공고가 나오면 알려요` : '같은 이름으로 새 공고가 나오면 알려요'} · 결과 발표 · 설명회 · 수행기관 모집은 문자 없이 화면에만</div>
            {pv === null && <div style={{ fontSize: T.xs, color: C.muted }}>확인하는 중…</div>}
            {pv && pv.length === 0 && <div style={{ fontSize: T.xs, color: C.orange }}>지난 1년 이 말로 잡힌 공고가 없어요 — 공고 제목에 쓰는 이름인지 확인해 주세요</div>}
            {openNow.length > 0 && (
              <div style={{ fontSize: T.xs, color: '#92400E', background: '#FEF3C7', border: '1px solid #FDE68A', borderRadius: R.sm, padding: '6px 8px', marginBottom: 6 }}>
                지금 접수 중인 공고가 이미 있어요 — 기다리기 전에 나온 공고라 알림은 가지 않아요. 바로 확인해 주세요.
              </div>
            )}
            {pv && pv.slice(0, 6).map(p => {
              const tag = p.noise ? [`${p.noise}`, C.muted] : p.old ? ['작년 공고', C.muted] : p.open ? ['지금 접수 중', '#92400E'] : ['이런 공고면 알림', C.green];
              return (
                <div key={p.id} style={{ display: 'flex', gap: 8, alignItems: 'baseline', padding: '3px 0', fontSize: T.xs }}>
                  <span style={{ width: 92, flexShrink: 0, color: tag[1], fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{tag[0]}</span>
                  <span style={{ flex: 1, minWidth: 0, color: C.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {p.url ? <a href={p.url} target="_blank" rel="noopener noreferrer" style={{ color: C.text, textDecoration: 'none' }}>{p.title}</a> : p.title}
                  </span>
                  <span style={{ flexShrink: 0, color: C.muted }}>{md(p.registered_at)}</span>
                </div>
              );
            })}
          </div>
        </>
      )}

      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <button type="button" onClick={onCancel} style={btn()}>취소</button>
        <button type="button" onClick={() => void save()} disabled={!picked || saving} style={{ ...btn('primary'), opacity: !picked || saving ? 0.5 : 1 }}>{saving ? '저장 중…' : '저장'}</button>
      </div>
    </div>
  );
}
